import {
  Breadcrumb as ChakraBreadcrumb,
  BreadcrumbItem as ChakraBreadcrumbItem,
  ComponentWithAs,
  Flex,
  IconProps,
  Text,
} from "@chakra-ui/react"
import { Button } from "DesignSystem/components/Button"
import { ChevronRightIcon } from "Icons/ChevronRightIcon"
import React from "react"
import { Link } from "react-router-dom"

export type BreadcrumbItem = {
  name: string
  to?: string
  isCurrentPage?: boolean
  shortenedAsIcon?: ComponentWithAs<"svg", IconProps>
}

type Props = {
  breadcrumbsData: BreadcrumbItem[]
  replaceCurrentPageWith?: React.ReactNode
}

/**
 * - The `breadcrumbsData` array can contain the following keys:
 *   - `name` the text to display
 *   - `isCurrentPage` style the breadcrumb item as the current page
 *   - `to` make a breadcrumb item navigate somewhere (via a React Router link)
 *   - `shortenedAsIcon` show an icon next to the label; the label becomes just
 *      the icon when there are 3+ breadcrumb items
 * - Use `replaceCurrentPageWith` to replace the current page's breadcrumb item with a custom component
 *   - e.g. when the final breadcrumb is the current title of an interview and
 *     you want to use an inline editor to let users rename the study
 *
 * @example
 * <Breadcrumb
 *   breadcrumbsData={[
 *     { name: "Dashboard", to: ROUTES.DASHBOARD.path, shortenedAsIcon: HomeIcon },
 *     { name: "Projects", to: ROUTES.PROJECTS.path },
 *     { name: "Project 1", isCurrentPage: true },
 *   ]}
 *   replaceCurrentPageWith={
 *     <InlineEditorComponent {...} />
 *   }
 * />
 */
export const Breadcrumb: React.FC<Props> = ({
  breadcrumbsData,
  replaceCurrentPageWith,
}) => (
  <ChakraBreadcrumb
    textStyle="ds.interface.medium"
    color="ds.text.subtle"
    width="100%"
    separator={<ChevronRightIcon boxSize={4} color="ds.icon.subtle" />}
    spacing={1}
  >
    {breadcrumbsData.map((breadcrumb, index) => {
      if (replaceCurrentPageWith && breadcrumb.isCurrentPage)
        return replaceCurrentPageWith

      return (
        <ChakraBreadcrumbItem
          key={`breadcrumb-${breadcrumb.name}-${index}`}
          isCurrentPage={breadcrumb.isCurrentPage}
        >
          <ButtonOrLabel
            to={breadcrumb.to}
            isCurrentPage={breadcrumb.isCurrentPage}
            ariaLabel={breadcrumb.name}
          >
            {index === 0 && breadcrumb.shortenedAsIcon ? (
              <ShortenedBreadcrumbItem
                name={breadcrumb.name}
                icon={breadcrumb.shortenedAsIcon}
                noOfBreadcrumbs={breadcrumbsData.length}
              />
            ) : (
              breadcrumb.name
            )}
          </ButtonOrLabel>
        </ChakraBreadcrumbItem>
      )
    })}
  </ChakraBreadcrumb>
)

const ButtonOrLabel: React.FC<
  React.PropsWithChildren<{
    to?: string
    isCurrentPage?: boolean
    ariaLabel: string
  }>
> = ({ to, isCurrentPage, ariaLabel, children }) => {
  if (to && !isCurrentPage) {
    return (
      <Button
        as={Link}
        to={to}
        variant={isCurrentPage ? "secondary" : "subtle"}
        size="flush"
        noOfLines={1}
        aria-label={ariaLabel}
        // This is a hack to correct for the changed line height caused by
        // the CSS rules applied by the noOfLines prop above. Removing both
        // props looks the same in terms of positioning, except it wraps
        // instead of truncating.
        lineHeight={1.2}
      >
        {children}
      </Button>
    )
  }

  return (
    // Made to look like a subtle button but without the clicky part
    <Text
      textStyle="ds.interface.medium"
      color={isCurrentPage ? "ds.text.default" : "ds.text.subtle"}
      noOfLines={1}
      // This is a hack to correct for the changed line height caused by
      // the CSS rules applied by the noOfLines prop above. Removing both
      // props looks the same in terms of positioning, except it wraps
      // instead of truncating.
      lineHeight={1.2}
    >
      {children}
    </Text>
  )
}

const ShortenedBreadcrumbItem: React.FC<{
  name: string
  icon: ComponentWithAs<"svg", IconProps>
  noOfBreadcrumbs: number
}> = ({ name, noOfBreadcrumbs, icon }) => {
  const Icon = icon
  return (
    <Flex align="center" gap={1.5}>
      <Icon boxSize={4} color="ds.icon.subtle" />
      {noOfBreadcrumbs <= 2 && <Text as="span">{name}</Text>}
    </Flex>
  )
}
